import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { SlidersHorizontal } from "lucide-react";

export default function FilterPanel({ value, onChange }) {
  const [local, setLocal] = React.useState(value);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (open) setLocal(value);
  }, [open, value]);

  const reset = () =>
    setLocal({
      minPrice: 20,
      maxPrice: 200,
      minAge: 18,
      maxAge: 65,
      minRating: 0,
      availableNow: false,
      radiusKm: 25,
    });

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          className="rounded-full border-stone-300 bg-white gap-2 shadow-sm shrink-0 h-11"
          data-testid="open-filters-button"
        >
          <SlidersHorizontal className="h-4 w-4" /> Filters
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="filter-panel">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl">Filters</SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-7">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-[0.1em] text-stone-500 font-bold">
                Price / hour
              </Label>
              <div className="text-sm font-medium text-stone-700">
                ${local.minPrice} – ${local.maxPrice}
              </div>
            </div>
            <Slider
              value={[local.minPrice, local.maxPrice]}
              min={20}
              max={300}
              step={5}
              onValueChange={(v) => setLocal({ ...local, minPrice: v[0], maxPrice: v[1] })}
              className="mt-4"
              data-testid="filter-price-slider"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-[0.1em] text-stone-500 font-bold">Age</Label>
              <div className="text-sm font-medium text-stone-700">
                {local.minAge} – {local.maxAge}
              </div>
            </div>
            <Slider
              value={[local.minAge, local.maxAge]}
              min={18}
              max={75}
              step={1}
              onValueChange={(v) => setLocal({ ...local, minAge: v[0], maxAge: v[1] })}
              className="mt-4"
              data-testid="filter-age-slider"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-[0.1em] text-stone-500 font-bold">
                Min rating
              </Label>
              <div className="text-sm font-medium text-stone-700">
                {local.minRating === 0 ? "Any" : `${local.minRating.toFixed(1)}★`}
              </div>
            </div>
            <Slider
              value={[local.minRating]}
              min={0}
              max={5}
              step={0.5}
              onValueChange={(v) => setLocal({ ...local, minRating: v[0] })}
              className="mt-4"
              data-testid="filter-rating-slider"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.1em] text-stone-500 font-bold">
              Search radius
            </Label>
            <div className="flex gap-2 mt-3">
              {[5, 10, 25, 50].map((r) => (
                <button
                  key={r}
                  onClick={() => setLocal({ ...local, radiusKm: r })}
                  className={`flex-1 py-2 rounded-xl text-sm border transition ${
                    local.radiusKm === r
                      ? "bg-[#4A5D4E] text-white border-[#4A5D4E]"
                      : "bg-white border-stone-300 text-stone-700 hover:bg-stone-50"
                  }`}
                  data-testid={`filter-radius-${r}`}
                >
                  {r} km
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl bg-stone-50 border border-stone-200">
            <div>
              <div className="font-medium text-stone-900">Available now</div>
              <div className="text-xs text-stone-500">Show only companions online right now</div>
            </div>
            <Switch
              checked={local.availableNow}
              onCheckedChange={(v) => setLocal({ ...local, availableNow: v })}
              data-testid="filter-available-now"
            />
          </div>
        </div>

        <SheetFooter className="mt-8 flex-row gap-3">
          <Button
            variant="outline"
            className="flex-1 rounded-full"
            onClick={reset}
            data-testid="filter-reset"
          >
            Reset
          </Button>
          <Button
            className="flex-1 rounded-full bg-[#4A5D4E] hover:bg-[#3B4A3E] text-white"
            onClick={() => {
              onChange(local);
              setOpen(false);
            }}
            data-testid="filter-apply"
          >
            Apply filters
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
